import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { endOfDay, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import { Box, CircularProgress, Pagination, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CalendarMonth as CalendarIcon, CheckCircle as CheckIcon, EventBusy as AbsentIcon,
  InsertChartOutlined as ChartIcon
} from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AlertBlock from 'components/shared/blocks/AlertBlock';
import SessionFilters from 'components/session/SessionFilters';
import SessionListItem from 'components/session/SessionListItem';
import SessionMarkMenu from 'components/session/SessionMarkMenu';
import SessionCounters from 'components/session/SessionCounters';
import SessionDetailDrawer from 'components/session/SessionDetailDrawer';
import SessionDetailPanel from 'components/session/SessionDetailPanel';
import StatusGroupHeader, { STATUS_GROUP_META, STATUS_GROUP_ORDER } from 'components/session/SessionStatusGroupHeader';
import { sessionStatus } from 'common/constants/options';
import { timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { normalizeSessionStatus } from 'common/utils/sessionColor';
import routes from 'common/routes';
import { isValidId } from 'common/utils/id';
import AppBarContext from 'context/AppBarContext';
import sessionSlice from 'store/modules/session';

const FETCH_SIZE = 200; // ventana reciente cargada de una vez (counters + donut sobre lo cargado).
const PER_PAGE = 25;
const INITIAL_FILTERS = { clientId: '', status: '', timeRange: '', date: {} };
const ORDER = STATUS_GROUP_ORDER;
const rangeLabel = (s) => `${convertUtcToLocalTime(s.start_date, timeFormat)} – ${convertUtcToLocalTime(s.end_date, timeFormat)}`;

/**
 * "Mis sesiones" del ENTRENADOR: misma "maravilla" que el cliente — cards-counter
 * (Programadas/Completadas/Ausencias/Asistencia), filtros, lista agrupada por estado y panel
 * lateral de detalle con donut. Además, cada card trae el menú "Marcar como" (Completó / Cliente
 * ausente / Entrenador ausente) y el detalle muestra al CLIENTE y quién faltó.
 */
function TrainerSessionsDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation(['labels', 'routes', 'sessions', 'tables']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('session.mySessions', { ns: 'routes' });

  const userId = useSelector((state) => state.auth.user.id);
  const sessions = useSelector((state) => state.session.data.list);
  const status = useSelector((state) => state.session.status.general);
  const refresh = useSelector((state) => state.session.refresh);

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [counterKey, setCounterKey] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Carga de una vez la ventana reciente del entrenador (filtro por fecha al servidor si está puesto).
  useEffect(() => {
    if (!userId) return;
    dispatch(sessionSlice.actions.list({
      pagination: { first: FETCH_SIZE, page: 1 },
      filters: {
        trainer_id: userId,
        start_between: { start_date: filters.date?.start_date || null, end_date: filters.date?.end_date || null }
      },
      orderBy: { column: 'START_DATE', direction: 'DESC' }
    }));
  }, [dispatch, userId, filters.date, refresh]);

  const items = useMemo(() => sessions
    .map((s) => ({ session: s, norm: normalizeSessionStatus(s.status, s.start_date), date: new Date(s.start_date).getTime() })), [sessions]);

  const counts = useMemo(() => items.reduce((acc, it) => {
    if (it.norm === 'scheduled' || it.norm === 'pending') acc.scheduled += 1;
    else if (it.norm === 'completed') acc.completed += 1;
    else if (it.norm === 'absent') acc.absent += 1;
    return acc;
  }, { scheduled: 0, completed: 0, absent: 0 }), [items]);

  const attendancePct = counts.completed + counts.absent > 0
    ? Math.round((counts.completed / (counts.completed + counts.absent)) * 100) : 0;

  const clientOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    items.forEach(({ session: s }) => {
      const name = s.client_id?.full_name;
      if (name && !seen.has(name)) { seen.add(name); opts.push({ value: name, label: name }); }
    });
    return opts;
  }, [items]);

  const timeRangeOptions = useMemo(() => {
    const set = new Set();
    items.forEach(({ session: s }) => set.add(rangeLabel(s)));
    return [...set].sort();
  }, [items]);

  const statusOptions = [
    { value: 'all', label: t('allStatuses', { ns: 'sessions', defaultValue: 'Todos los estados' }) },
    { value: 'scheduled', label: t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' }) },
    { value: 'completed', label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }) },
    { value: 'absent', label: t('absences', { ns: 'sessions', defaultValue: 'Ausencias' }) }
  ];

  const filteredItems = useMemo(() => items
    .filter(({ session: s, norm }) => {
      const effectiveStatus = counterKey || filters.status;
      if (effectiveStatus && !(norm === effectiveStatus || (effectiveStatus === 'scheduled' && norm === 'pending'))) return false;
      if (filters.clientId && s.client_id?.full_name !== filters.clientId) return false;
      if (filters.timeRange && rangeLabel(s) !== filters.timeRange) return false;
      if (filters.date?.start_date && isBefore(parseISO(s.start_date), startOfDay(parseISO(filters.date.start_date)))) return false;
      if (filters.date?.end_date && isAfter(parseISO(s.start_date), endOfDay(parseISO(filters.date.end_date)))) return false;
      return true;
    })
    .sort((a, b) => {
      const oa = ORDER[a.norm] ?? 3;
      const ob = ORDER[b.norm] ?? 3;
      if (oa !== ob) return oa - ob;
      return oa <= 1 ? a.date - b.date : b.date - a.date;
    }), [items, filters, counterKey]);

  const pageCount = Math.ceil(filteredItems.length / PER_PAGE) || 1;
  const pagedItems = useMemo(() => filteredItems.slice((page - 1) * PER_PAGE, page * PER_PAGE), [filteredItems, page]);

  const counters = [
    { key: 'scheduled', label: t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' }), value: counts.scheduled, sub: t('upcomingClasses', { ns: 'sessions', defaultValue: 'Próximas clases' }), color: theme.palette.sessionProximity.far, icon: <CalendarIcon /> },
    { key: 'completed', label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }), value: counts.completed, sub: t('thisMonth', { ns: 'sessions', defaultValue: 'Este mes' }), color: theme.palette.sessionStatus.completed.fg, icon: <CheckIcon /> },
    { key: 'absent', label: t('absences', { ns: 'sessions', defaultValue: 'Ausencias' }), value: counts.absent, sub: t('thisMonth', { ns: 'sessions', defaultValue: 'Este mes' }), color: theme.palette.sessionStatus.absent.fg, icon: <AbsentIcon /> },
    { key: 'attendance', label: t('attendance', { ns: 'sessions', defaultValue: 'Asistencia' }), value: `${attendancePct}%`, sub: t('last30days', { ns: 'sessions', defaultValue: 'Últimos 30 días' }), color: theme.palette.gold.bright, icon: <ChartIcon />, disabled: true }
  ];

  const selectedItem = useMemo(() => items.find((it) => String(it.session.id) === String(selectedId)), [items, selectedId]);
  const progress = { completed: counts.completed, scheduled: counts.scheduled, absent: counts.absent };

  const onMark = useCallback((id, newStatus) => {
    dispatch(sessionSlice.actions.update({ id, input: { status: newStatus } }));
  }, [dispatch]);

  const goToAgenda = useCallback((s) => {
    const day = convertUtcToLocalTime(s.start_date, 'yyyy-MM-dd');
    // Ver la nota de LargeSessionCard: un id inservible dejaba la agenda reintentando sin fin.
    const sessionParam = isValidId(s?.id) ? `&session=${s.id}` : '';
    navigate(`${routes.calendar.path}?date=${day}${sessionParam}`);
  }, [navigate]);

  const toggleCounter = useCallback((key) => {
    setCounterKey((prev) => (prev === key ? null : key));
    setPage(1);
  }, []);

  const isLoading = status === 'loading' && sessions.length === 0;

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('trainerSessionsSubtitle', { ns: 'sessions', defaultValue: 'Revisa y reporta tus sesiones individuales.' })}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <SessionCounters counters={counters} activeKey={counterKey} onSelect={toggleCounter} />
      </Box>

      <SessionFilters
        clientOptions={clientOptions}
        timeRangeOptions={timeRangeOptions}
        statusOptions={statusOptions}
        onApply={(v) => { setFilters(v); setCounterKey(null); setPage(1); }}
      />

      <AlertBlock type="sessionUpdate" />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: 'secondary.main' }} /></Box>
      ) : (
        <>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {filteredItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">{t('noSessionsFilter', { ns: 'sessions', defaultValue: 'No hay sesiones que mostrar. Intenta cambiando los filtros' })}</Typography>
              </Box>
            ) : (() => {
              let lastGroup = null;
              const nodes = [];
              pagedItems.forEach(({ session: s, norm }) => {
                if (norm !== lastGroup) {
                  const meta = STATUS_GROUP_META[norm] || STATUS_GROUP_META.scheduled;
                  nodes.push(
                    <StatusGroupHeader
                      key={`grp-${norm}-${s.id}`}
                      label={t(meta.key, { ns: 'sessions', defaultValue: meta.def })}
                      color={theme.palette.sessionStatus[meta.path].fg}
                      first={nodes.length === 0}
                    />
                  );
                  lastGroup = norm;
                }
                nodes.push(
                  <SessionListItem
                    key={s.id}
                    session={s}
                    dateLocale={dateLocale}
                    person={s.client_id}
                    personLabel={t('client', { count: 1, ns: 'labels', defaultValue: 'Cliente' })}
                    selected={String(s.id) === String(selectedId)}
                    onAction={() => setSelectedId(s.id)}
                    actionNode={(
                      <SessionMarkMenu onMark={(st) => onMark(s.id, st)} disabled={s.status !== sessionStatus.scheduled} />
                    )}
                  />
                );
              });
              return nodes;
            })()}
          </Stack>
          {pageCount > 1 && (
            <Stack alignItems="center" sx={{ mt: 3 }}>
              <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} color="secondary" shape="rounded" />
            </Stack>
          )}
        </>
      )}

      <SessionDetailDrawer open={Boolean(selectedItem)} onClose={() => setSelectedId(null)}>
        {selectedItem ? (
          <SessionDetailPanel
            session={selectedItem.session}
            displayStatus={selectedItem.session.status}
            dateLocale={dateLocale}
            person={selectedItem.session.client_id}
            personLabel={t('client', { count: 1, ns: 'labels', defaultValue: 'Cliente' })}
            progress={progress}
            actionNode={selectedItem.session.status === sessionStatus.scheduled ? (
              <SessionMarkMenu onMark={(st) => { onMark(selectedItem.session.id, st); setSelectedId(null); }} />
            ) : null}
            onClose={() => setSelectedId(null)}
            onGoToAgenda={() => goToAgenda(selectedItem.session)}
          />
        ) : <Box />}
      </SessionDetailDrawer>
    </>
  );
}

export default TrainerSessionsDashboard;
